import React from "react";
import { Button, Form, ListGroup } from "react-bootstrap";
import { FiArrowDown, FiArrowUp, FiEye, FiTrash2 } from "react-icons/fi";
import StatusPill from "@core/components/StatusPill";
import { TEMPLATE_PREVIEW_LABEL } from "@core/reports/components/TemplateRowPreviewButton";

export const ASSIGN_MOVE_UP_LABEL = "Move up";
export const ASSIGN_MOVE_DOWN_LABEL = "Move down";

type Props = {
    title: string;
    sublabel?: string;
    isActive?: boolean;
    listOrder: number;
    listLength: number;
    onOrderChange: (newOrder: number) => void;
    onRemove: () => void;
    onPreview?: () => void;
    showRequiredFields?: boolean;
    isRequired?: boolean;
    onRequiredChange?: (required: boolean) => void;
    requiredAt?: string | null;
    onRequiredAtChange?: (value: string) => void;
    requiredSwitchId: string;
};

const AssignAssignedRow: React.FC<Props> = ({
    title,
    sublabel,
    isActive = true,
    listOrder,
    listLength,
    onOrderChange,
    onRemove,
    onPreview,
    showRequiredFields = false,
    isRequired = false,
    onRequiredChange,
    requiredAt,
    onRequiredAtChange,
    requiredSwitchId,
}) => (
    <ListGroup.Item className="py-2 px-3">
        <div className="d-flex align-items-start justify-content-between gap-2 mb-1">
            <div className="min-w-0 flex-grow-1">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="fw-semibold">{title}</div>
                    {!isActive ? <StatusPill active={false} /> : null}
                </div>
                {sublabel ? <div className="text-muted small">{sublabel}</div> : null}
            </div>
            <div className="d-flex align-items-start gap-1 flex-shrink-0">
                {onPreview ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        className="assign-icon-btn"
                        onClick={onPreview}
                        title={TEMPLATE_PREVIEW_LABEL}
                        aria-label={TEMPLATE_PREVIEW_LABEL}
                    >
                        <FiEye aria-hidden />
                    </Button>
                ) : null}
                <Button
                    type="button"
                    size="sm"
                    variant="outline-danger"
                    className="assign-icon-btn"
                    onClick={onRemove}
                    title="Remove"
                >
                    <FiTrash2 />
                </Button>
            </div>
        </div>
        <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
            <div className="d-flex align-items-center gap-1">
                <Form.Label className="small mb-0 text-nowrap">Order:</Form.Label>
                <Form.Control
                    type="number"
                    size="sm"
                    min={1}
                    max={listLength}
                    value={listOrder}
                    onChange={(e) =>
                        onOrderChange(Math.max(1, Number(e.target.value) || 1))
                    }
                    style={{ width: 70 }}
                />
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="assign-icon-btn"
                    disabled={listOrder <= 1}
                    onClick={() => onOrderChange(listOrder - 1)}
                    title={ASSIGN_MOVE_UP_LABEL}
                    aria-label={ASSIGN_MOVE_UP_LABEL}
                >
                    <FiArrowUp aria-hidden />
                </Button>
                <Button
                    type="button"
                    size="sm"
                    variant="outline-secondary"
                    className="assign-icon-btn"
                    disabled={listOrder >= listLength}
                    onClick={() => onOrderChange(listOrder + 1)}
                    title={ASSIGN_MOVE_DOWN_LABEL}
                    aria-label={ASSIGN_MOVE_DOWN_LABEL}
                >
                    <FiArrowDown aria-hidden />
                </Button>
            </div>
            {showRequiredFields ? (
                <>
                    <Form.Check
                        type="switch"
                        id={requiredSwitchId}
                        label="Required"
                        checked={isRequired}
                        onChange={(e) => onRequiredChange?.(e.target.checked)}
                        className="small mb-0"
                    />
                    {isRequired ? (
                        <div className="d-flex align-items-center gap-1">
                            <Form.Label
                                htmlFor={`${requiredSwitchId}-date`}
                                className="small mb-0 text-nowrap"
                            >
                                Required starting:
                            </Form.Label>
                            <Form.Control
                                id={`${requiredSwitchId}-date`}
                                type="date"
                                size="sm"
                                value={requiredAt ?? ""}
                                onChange={(e) => onRequiredAtChange?.(e.target.value)}
                                style={{ maxWidth: 160 }}
                            />
                        </div>
                    ) : null}
                </>
            ) : null}
        </div>
    </ListGroup.Item>
);

export default AssignAssignedRow;
